import type { MeetingStatus } from "@/services/api/meetings/types";
import type { StatusDotColor } from "@/components/ui/status-dot";

/** Badge palette keys from `@/components/ui/badge/Badge`. */
export type MeetingStatusBadgeColor =
  | "zinc"
  | "blue"
  | "green"
  | "red"
  | "amber"
  | "emerald"
  | "indigo";

export function getMeetingStatusBadgeColor(
  status: MeetingStatus | string,
  opts?: { transcriptionFailed?: boolean }
): MeetingStatusBadgeColor {
  if (opts?.transcriptionFailed && String(status) === "needs_upload") {
    return "red";
  }
  switch (status) {
    case "scheduled":
      return "blue";
    case "awaiting_capture":
      return "amber";
    case "capturing":
    case "transcribing":
      return "indigo";
    case "needs_upload":
    case "needs_review":
      return "amber";
    case "approved":
      return "green";
    case "analysed":
      return "emerald";
    case "cancelled":
      return "zinc";
    default:
      return "zinc";
  }
}

/** StatusDot colour for tables and meta (not coloured badge pills). */
export function meetingStatusDotColor(
  status: MeetingStatus | string,
  opts?: { transcriptionFailed?: boolean }
): StatusDotColor {
  switch (getMeetingStatusBadgeColor(status, opts)) {
    case "blue":
      return "blue";
    case "amber":
      return "amber";
    case "indigo":
      return "indigo";
    case "green":
      return "green";
    case "emerald":
      return "emerald";
    case "red":
      return "red";
    default:
      return "gray";
  }
}

export function meetingStatusIndicatorClass(
  status: MeetingStatus | string,
  opts?: { transcriptionFailed?: boolean }
): string {
  if (opts?.transcriptionFailed && String(status) === "needs_upload") {
    return "bg-red-500";
  }
  switch (status) {
    case "scheduled":
      return "bg-indigo-500";
    case "awaiting_capture":
    case "needs_upload":
    case "needs_review":
      return "bg-amber-500";
    case "capturing":
    case "transcribing":
      return "bg-indigo-400";
    case "approved":
    case "analysed":
      return "bg-emerald-500";
    case "cancelled":
      return "bg-slate-400";
    default:
      return "bg-slate-400";
  }
}

/** Shared status vocabulary with mobile `formatMeetingStatusLabel`. */
export function formatMeetingStatusLabel(
  status: MeetingStatus | string,
  opts?: { transcriptionFailed?: boolean }
): string {
  const s = String(status);
  switch (s) {
    case "scheduled":
      return "Scheduled";
    case "awaiting_capture":
      return "Awaiting recording";
    case "capturing":
      return "Recording";
    case "transcribing":
      return "Transcribing";
    case "needs_upload":
      return opts?.transcriptionFailed ? "Transcription failed" : "Needs recording";
    case "needs_review":
      return "Notes to review";
    case "approved":
      return "Approved";
    case "analysed":
      return "Analysed";
    case "cancelled":
      return "Cancelled";
    default:
      return s
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
  }
}

/** True when the meeting needs work but its latest capture failed. */
export function isTranscriptionFailed(
  status: MeetingStatus | string | null | undefined,
  latestCaptureStatus?: string | null
): boolean {
  return (
    String(status ?? "") === "needs_upload" &&
    String(latestCaptureStatus ?? "").toLowerCase() === "failed"
  );
}

/** Latest capture status, preferring the backend summary. */
export function latestCaptureStatusForDetail(detail: {
  captures?: { status?: string | null }[];
  captureSummary?: { latestStatus?: string | null } | null;
}): string | null {
  const fromSummary = detail.captureSummary?.latestStatus?.trim();
  if (fromSummary) return fromSummary;
  const captures = detail.captures ?? [];
  if (!captures.length) return null;
  const latest = captures[captures.length - 1]?.status?.trim();
  return latest || null;
}

/** Statuses that can have a transcript — others skip the fetch (a 404). */
export const transcriptReadyStatuses = new Set([
  "needs_review",
  "approved",
  "analysed",
]);

export function hasTranscriptForStatus(
  status: MeetingStatus | string | null | undefined
): boolean {
  return transcriptReadyStatuses.has(String(status ?? "").toLowerCase());
}

/** RSVP label for a calendar attendee. */
export function formatAttendeeResponse(response?: string | null): string {
  switch (String(response ?? "").trim().toLowerCase()) {
    case "accepted":
      return "Accepted";
    case "tentative":
      return "Tentative";
    case "declined":
      return "Declined";
    default:
      return "No response";
  }
}

/** Transcript review states from meetings API (`draft` | `approved`). */
export function getTranscriptStatusBadgeColor(
  status: string
): MeetingStatusBadgeColor {
  switch (status) {
    case "draft":
      return "amber";
    case "approved":
      return "green";
    default:
      return "zinc";
  }
}

/** Subscribe on `/ws/tasks` when the backend has queued a runner task for this meeting. */
export function meetingHasActiveTask(meeting: {
  currentTaskId?: string | null;
}): boolean {
  return Boolean(meeting.currentTaskId && meeting.currentTaskId.length > 0);
}
